import React, { useEffect, useState } from "react";

import {
  DialogTitle,
  DialogContent,
  Grid,
  Tooltip,
  Button,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  IconButton,
  Link,
  TextField,
  Alert,
} from "@mui/material";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DoNotDisturbOnOutlinedIcon from "@mui/icons-material/DoNotDisturbOnOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";

import { StyledDialog, classes } from "./styles";
import { mspIntegrationMetadata } from "./constants";
import { MSPIntegrationLogo } from "views/Pages/MSPPortal/MSPIntegrationLogo";

import { PSAIntegrationTest } from "lib/models/PSAIntegrationTest";
import MSPIntegrationAccountMapping from "./AccountMapping/accountMapping";
import { PSAIntegration } from "lib/models/PSAIntegration";

const betaMSPIntegrationTooltipText =
  "Donut I love apple pie I love jelly-o I love I love lemon drops pie. Carrot cake caramels I love gummies biscuit macaroon marshmallow."; // TODO: figure out what this beta text needs to be...until then enjoy some cupcake ipsum :)

const MSPIntegrationDialog = (props) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [domainName, setDomainName] = useState("");
  const [publicKey, setPublicKey] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [isConnectionButtonDisabled, setIsConnectionButtonDisabled] =
    useState(true);
  const [isTestSuccessful, setIsTestSuccessful] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSecondAccordionOpen, setIsSecondAccordionOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [expandAccordionOne, setExpandAccordionOne] = useState(true);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isIntegrationConnected, setIsIntegrationConnected] = useState(false);

  useEffect(() => {
    if (props.editingModel) {
      setSelectedModel(props.editingModel);
      setCurrentStep(1);
    }
  }, [props.editingModel, props.open]);

  useEffect(() => {
    // If one of the fields doesn't have a value, make sure the button is disabled
    if (
      !domainName.length ||
      !companyId.length ||
      !publicKey.length ||
      !privateKey.length
    ) {
      setIsConnectionButtonDisabled(true);
    } else {
      setIsConnectionButtonDisabled(false);
    }
  }, [domainName, companyId, publicKey, privateKey]);

  useEffect(() => {
    if (selectedModel && selectedModel.id) {
      if (selectedModel.manageUrl) {
        setDomainName(selectedModel.manageUrl);
      }

      if (selectedModel.companyId) {
        setCompanyId(selectedModel.companyId);
      }
    }
  }, [selectedModel]);

  const getMSPIntegrationList = () => {
    // Only show integrations that are generally available OR the the org has the beta license feature enabled for
    return mspIntegrationMetadata.filter(
      (module) => !module.betaChip || props.license.hasFeature(module?.betaChip)
    );
  };

  const isModuleInBeta = (module) => {
    const { integrationType = "" } = module; // eslint-disable-line no-unused-vars

    return false;
  };

  const isModuleNew = (module) => {
    const { integrationType = "" } = module; // eslint-disable-line no-unused-vars

    return false;
  };

  const handleSelectModule = (module) => {
    setSelectedModel(new PSAIntegration());
    setIsIntegrationConnected(false);
    setCurrentStep(1);
  };

  const handleDomainNameChange = (event) => {
    setDomainName(event.target.value);
  };

  const handlePublicKeyChange = (event) => {
    setPublicKey(event.target.value);
  };

  const handlePrivateKeyChange = (event) => {
    setPrivateKey(event.target.value);
  };

  const handleCompanyIdChange = (event) => {
    setCompanyId(event.target.value);
  };

  const handleAccordionChange = (_, expanded) => {
    setIsSecondAccordionOpen(expanded);
  };

  const IntegrationButton = (props) => {
    return (
      <Grid item xs={3} className={classes.buttonsGrid}>
        <Button
          variant={"outlined"}
          disabled={props.disabled}
          className={classes.moduleButton}
          datacy={props.module.integrationType}
          onClick={() => {
            // reference paidCta prop so that user can still
            // select to click on the cta link in the card
            if (props.paidCta) return;
            handleSelectModule(props.module);
          }}
        >
          <div className={classes.moduleOptionContainer}>
            <div className={classes.logoCtaContainer}>
              <div className={classes.logoContainer}>
                <MSPIntegrationLogo
                  integrationType={props.module.integrationType}
                  width={120}
                  height={66}
                />
              </div>
            </div>
            <div className={classes.textContainer}>
              <p className={classes.moduleTitleText}>{props.module.title}</p>
              <p className={classes.moduleSubtitleText}>
                {props.module.subtitle}
              </p>
            </div>
          </div>
        </Button>
        {props.paidCta ? (
          <div className={classes.paidPlanCtaContainer}>
            <p>Available on paid plans</p>
            <div>
              <a target={"_blank"} rel="noopener noreferrer" href={"#"}>
                Contact sales
                <OpenInNewIcon />
              </a>
            </div>
          </div>
        ) : props.isBeta ? (
          <Tooltip
            title={betaMSPIntegrationTooltipText}
            placement={"bottom-start"}
            classes={{ tooltip: classes.tooltip }}
          >
            <div
              className={`${classes.paidPlanCtaContainer} ${classes.betaTooltipCtaContainer}`}
            >
              <p>Beta</p>
              <InfoOutlinedIcon />
            </div>
          </Tooltip>
        ) : props.isNew ? (
          <div
            className={`${classes.paidPlanCtaContainer} ${classes.betaTooltipCtaContainer}`}
          >
            <p>New</p>
            <AutoAwesomeIcon />
          </div>
        ) : null}
      </Grid>
    );
  };

  const handleConnectionButton = () => {
    const testPSAModel = new PSAIntegrationTest();
    const body = {
      companyId: companyId,
      publicKey: publicKey,
      privateKey: privateKey,
      manageUrl: domainName,
    };
    setIsTestingConnection(true);
    setErrorMessage("");
    testPSAModel.set(body);

    // Hit the POST endpoint for testing an integration
    testPSAModel
      .create()
      .then(() => {
        // Show success text and change button
        setIsTestSuccessful(true);
        setIsTestingConnection(false);
      })
      .catch((error) => {
        setErrorMessage(
          "Failed to connect to ConnectWise API.  Please check your secret values and try again."
        );
        setIsTestingConnection(false);
      });
  };

  const handleSaveButton = () => {
    const body = {
      companyId: companyId,
      publicKey: publicKey,
      privateKey: privateKey,
      manageUrl: domainName,
    };

    setIsSaving(true);
    selectedModel.set(body);

    const crudMethod = selectedModel.id ? "update" : "create";
    selectedModel[crudMethod]()
      .then(() => {
        props.reload(true);
        setIsIntegrationConnected(true);
        setIsTestSuccessful(false);
        setExpandAccordionOne(false);
        setIsSaving(false);
      })
      .catch((error) => {
        setErrorMessage(
          "Failed to connect to ConnectWise API.  Please check your secret values and try again."
        ); // TODO: figure out with this use case error message should be
        setIsSaving(false);
      });
  };

  const handleAccordionOneChange = () => {
    setExpandAccordionOne(!expandAccordionOne);
  };

  const handleSaveAccountMapping = () => {
    console.warn("Account Mapping Saved");
  };

  const displayConnectedStatus = () => {
    if (
      isTestSuccessful ||
      isIntegrationConnected ||
      selectedModel?.connectionStatus === "OK"
    ) {
      return (
        <div className={classes.accordionOneConnectedChip}>
          <DoNotDisturbOnOutlinedIcon />
          Connected
        </div>
      );
    } else {
      return (
        <div className={classes.accordionOneNotConnectedChip}>
          <DoNotDisturbOnOutlinedIcon />
          Not Connected
        </div>
      );
    }
  };

  const displayDialogContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Grid
            container
            spacing={2}
            className={classes.moduleOptionsContainer}
            datacy={"mspIntegrationsListModal"}
          >
            {getMSPIntegrationList().map((module, idx) => {
              return (
                <IntegrationButton
                  module={module}
                  key={module.integrationType}
                  isBeta={isModuleInBeta(module)}
                  isNew={isModuleNew(module)}
                />
              );
            })}
          </Grid>
        );
      case 1:
        return (
          <div>
            <div className={classes.titleWrap}>
              <Typography className={classes.stepOneAccordionText}>
                ConnectWise Manage One Way Syncing
              </Typography>
            </div>
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
            <Card className={classes.accordionCard}>
              <Accordion
                className={classes.accordionContainer}
                expanded={expandAccordionOne}
                onChange={handleAccordionOneChange}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                >
                  <div className={classes.accordionTitle}>
                    ConnectWise Manage API Connection
                    {displayConnectedStatus()}
                  </div>
                </AccordionSummary>
                <AccordionDetails className={classes.accordionOneContainer}>
                  <Link
                    href="#"
                    className={classes.setupGuideLink}
                    underline="hover"
                  >
                    Setup guide
                    <OpenInNewIcon />
                  </Link>
                  <TextField
                    variant="outlined"
                    label="ConnectWise Host (Domain Name)"
                    value={domainName}
                    onChange={handleDomainNameChange}
                    className={classes.accordionOneTextField}
                    placeholder="https://"
                  />
                  <TextField
                    variant="outlined"
                    label="Company ID"
                    value={companyId}
                    onChange={handleCompanyIdChange}
                    className={classes.accordionOneTextField}
                  />
                  <TextField
                    variant="outlined"
                    label="Public Key"
                    value={publicKey}
                    onChange={handlePublicKeyChange}
                    className={classes.accordionOneTextField}
                  />
                  <TextField
                    variant="outlined"
                    label="Private Key"
                    value={privateKey}
                    onChange={handlePrivateKeyChange}
                    className={classes.accordionOneTextField}
                  />
                  <div className={classes.buttonContainer}>
                    {isTestSuccessful ? (
                      <Button
                        variant="contained"
                        className={classes.accordionOneButton}
                        disabled={isConnectionButtonDisabled || isSaving}
                        onClick={handleSaveButton}
                      >
                        {isSaving
                          ? "Saving..."
                          : "Save and Continue to Account Mapping"}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        className={classes.accordionOneButton}
                        disabled={
                          isConnectionButtonDisabled || isTestingConnection
                        }
                        onClick={handleConnectionButton}
                      >
                        {isTestingConnection
                          ? "Testing Connection..."
                          : "Test Connection"}
                      </Button>
                    )}
                    {isTestSuccessful && (
                      <div className={classes.successContainer}>
                        <CheckIcon />
                        <p className={classes.successText}>
                          Successfully Connected
                        </p>
                      </div>
                    )}
                  </div>
                </AccordionDetails>
              </Accordion>
            </Card>
            <Card className={classes.accordionCard}>
              <Accordion
                className={classes.accordionContainer}
                onChange={handleAccordionChange}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel2-content"
                  id="panel2-header"
                >
                  <div className={classes.accordionTitle}>
                    Account Mapping
                    {/* Need to make sure this is still working when we are auto-opening accordions */}
                    {!isSecondAccordionOpen && (
                      <div className={classes.accordionSummaryDetails}>
                        <div>
                          Not Mapped
                          <div className={classes.accordionTwoNotMappedChip}>
                            2
                          </div>
                        </div>
                        <div>
                          Mapped
                          <div className={classes.accordionTwoMappedChip}>
                            51
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <MSPIntegrationAccountMapping></MSPIntegrationAccountMapping>
                  <Button
                    variant="contained"
                    className={classes.integrationMappingButton}
                    onClick={handleSaveAccountMapping}
                  >
                    Save Account Mapping
                  </Button>
                </AccordionDetails>
              </Accordion>
            </Card>
            <Card className={classes.accordionCard}>
              <Accordion className={classes.accordionContainer} disabled>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel3-content"
                  id="panel3-header"
                  className={classes.accordionTitle}
                >
                  Service Board
                </AccordionSummary>
                <AccordionDetails>Content to go here</AccordionDetails>
              </Accordion>
            </Card>
            <Card className={classes.accordionCard}>
              <Accordion className={classes.accordionContainer} disabled>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel4-content"
                  id="panel4-header"
                  className={classes.accordionTitle}
                >
                  Blumira-to-ConnectWise Ticket Flow
                </AccordionSummary>
                <AccordionDetails>Content to go here</AccordionDetails>
              </Accordion>
            </Card>
            <Card className={classes.accordionCard}>
              <Accordion className={classes.accordionContainer} disabled>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel5-content"
                  id="panel5-header"
                  className={classes.accordionTitle}
                >
                  Verify and Enable/Disable
                </AccordionSummary>
                <AccordionDetails>Content to go here</AccordionDetails>
              </Accordion>
            </Card>
          </div>
        );
    }
  };

  const displayDialogTitle = () => {
    return currentStep === 0
      ? "Available MSP Integrations"
      : "MSP Integration Details";
  };

  const resetModal = () => {
    setErrorMessage("");
    setExpandAccordionOne(true);
    resetAccordionOneValues();
    setTimeout(() => setCurrentStep(0), 200);
  };

  const toggleModal = () => {
    props.toggleModal();
    resetModal();
  };

  const resetAccordionOneValues = () => {
    setDomainName("");
    setCompanyId("");
    setPrivateKey("");
    setPublicKey("");
  };

  return (
    <StyledDialog
      open={props.open}
      onClose={toggleModal}
      maxWidth={"lg"}
      fullWidth
    >
      <DialogTitle className={classes.modalTitleWrap}>
        {displayDialogTitle()}
        <IconButton onClick={toggleModal}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>{displayDialogContent()}</DialogContent>
    </StyledDialog>
  );
};

export default MSPIntegrationDialog;
